/**
 * Design tokens — the bridge between the named palette (`colors.ts`) and the shapes
 * the rest of the app expects (MUI palette, gradients, shape and motion).
 *
 * Every colour value here is *derived* from `colors.ts`, so there is exactly one
 * place to change a brand colour.
 */

import { Color, colors, themeColors, withAlpha } from "./colors";

// ── Typefaces ─────────────────────────────────────────────────────
// All three are self-hosted through Fontsource (imported in `main.tsx`) rather than
// loaded from a CDN: the Capacitor bundle has to render correctly offline.

/** Headlines. Wide, geometric, always uppercase with generous tracking. */
export const FONT_HEADLINE = '"Syncopate", "Outfit", system-ui, sans-serif';
/** Subheadlines. The serif counterpoint that keeps the brand from reading generic. */
export const FONT_SUBHEAD = '"Gilda Display", Georgia, serif';
/** Body and UI. Legible at every size, carries Czech diacritics. */
export const FONT_BODY = '"Outfit Variable", "Outfit", system-ui, sans-serif';

// ── Brand ─────────────────────────────────────────────────────────
export const brand = {
  violet: colors[Color.Violet700],
  violetDeep: colors[Color.Violet900],
  violetLight: colors[Color.Violet500],
  violetSoft: colors[Color.Violet300],
  tagline: "MAGIC BY SCIENCE",
} as const;

// ── Signature gradients ───────────────────────────────────────────
export const gradients = {
  // Both page gradients end on `backgroundDeep`, which is the value the contrast guard
  // in `colors.test.ts` measures text against — the two must not drift apart.
  /** Science Silver gradient — the brand's default page background. */
  pageLight: `linear-gradient(160deg, ${themeColors.light.background} 0%, ${colors[Color.Silver300]} 55%, ${themeColors.light.backgroundDeep} 100%)`,
  /** Stem Black equivalent for dark mode. */
  pageDark: `linear-gradient(160deg, ${themeColors.dark.background} 0%, ${themeColors.dark.background} 55%, ${themeColors.dark.backgroundDeep} 100%)`,
  /**
   * The Energy Pulse — a soft violet bloom around a light organic core. Used as an
   * ambient background mark and as the fallback artwork for an article with no cover.
   */
  pulse: `radial-gradient(circle at 50% 45%, ${withAlpha(colors[Color.Silver100], 0.95)} 0%, ${withAlpha(
    colors[Color.Violet300],
    0.75,
  )} 32%, ${colors[Color.Violet700]} 62%, ${withAlpha(colors[Color.Violet900], 0.85)} 100%)`,
  /** Morning Elyxeer: warming, golden-hour, low contrast. */
  morning: `linear-gradient(135deg, ${colors[Color.Violet300]} 0%, ${colors[Color.Silver100]} 100%)`,
  /** Evening Elyxeer: cooling, vignetted, muted. */
  evening: `linear-gradient(135deg, ${colors[Color.Violet900]} 0%, ${colors[Color.Black900]} 100%)`,
  /** The brand's photo filter, as a CSS overlay. */
  photoOverlay: `linear-gradient(180deg, ${withAlpha(colors[Color.Black500], 0.0)} 0%, ${withAlpha(colors[Color.Violet900], 0.55)} 100%)`,
} as const;

// ── Per-scheme surface and text tokens ────────────────────────────
export const light = {
  bg: themeColors.light.background,
  paper: themeColors.light.surface,
  inset: themeColors.light.surfaceInset,
  text: themeColors.light.textPrimary,
  textSecondary: themeColors.light.textSecondary,
  textMuted: themeColors.light.textMuted,
  divider: themeColors.light.border,
  gradient: gradients.pageLight,
} as const;

export const dark = {
  bg: themeColors.dark.background,
  paper: themeColors.dark.surface,
  inset: themeColors.dark.surfaceInset,
  text: themeColors.dark.textPrimary,
  textSecondary: themeColors.dark.textSecondary,
  textMuted: themeColors.dark.textMuted,
  divider: themeColors.dark.border,
  gradient: gradients.pageDark,
} as const;

// ── Layout ────────────────────────────────────────────────────────
/**
 * Where the app stops being a phone and becomes a desktop application: the tab bar
 * is replaced by a side rail, the feed becomes a grid, and the type scale steps up.
 *
 * This is MUI's `md` breakpoint. It is re-exported here so the media queries baked
 * into the type scale cannot drift from the `sx` breakpoints used everywhere else.
 */
export const DESKTOP_BREAKPOINT = 900;

/** Media query for "desktop and up", for use inside plain style objects. */
export const UP_DESKTOP = `@media (min-width:${DESKTOP_BREAKPOINT}px)`;

/** Width of the permanent side rail on desktop. */
export const SIDE_NAV_WIDTH = 264;

/** Height of the tab bar on phones. Content clears it via `Screen`. */
export const BOTTOM_NAV_HEIGHT = 64;

/**
 * Comfortable measure for long-form article bodies. Text is capped at this even when
 * the column around it is wider — a 900px line of prose is unreadable.
 *
 * Deliberately px and not `ch`: `ch` is the advance of the "0" glyph, and Outfit's is
 * unusually wide, so `68ch` resolved to ~803px — roughly 89 characters per line. This
 * value lands around 70 at the 18px body size, inside the 45–75 comfortable range.
 */
export const READING_MEASURE = "640px";

// ── Shape ─────────────────────────────────────────────────────────
// The brand's containers are visibly round-cornered; nothing is square.
export const RADIUS = {
  sm: 10,
  md: 14,
  lg: 20,
  xl: 28,
  full: 999,
} as const;

// ── Motion ────────────────────────────────────────────────────────
// Calm and rhythmic, never bouncy — the brand is about stability, not spikes.
export const MOTION = {
  duration: 200,
  easing: "cubic-bezier(0.4, 0, 0.2, 1)",
  transition: "200ms cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

// ── Elevation ─────────────────────────────────────────────────────
// Shadows are violet-tinted rather than neutral black, so raised surfaces stay warm.
export const SHADOW = {
  card: `0 1px 2px ${withAlpha(colors[Color.Violet900], 0.06)}, 0 8px 24px ${withAlpha(colors[Color.Violet900], 0.08)}`,
  raised: `0 2px 4px ${withAlpha(colors[Color.Violet900], 0.08)}, 0 16px 40px ${withAlpha(colors[Color.Violet900], 0.14)}`,
} as const;
